import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { JobChangeProcess } from '../../types/JobChangeProcess';
import { useAtom, useSetAtom } from 'jotai';
import {
  currentjobChangeProcessStatusAtom,
  currentSelectjobChangeProcessAtom,
  openedStatusMenuIdAtom,
} from '../../atoms/jobChangeProcess';
import clsx from 'clsx';

type Props = {
  jobChangeProcess: JobChangeProcess;
  statusModalOpen: () => void;
  /** トリガーに表示するラベル（未指定時は progress_name） */
  triggerLabel?: string;
};

export default function JobChangeProcessStatusMenu({
  jobChangeProcess,
  statusModalOpen,
  triggerLabel,
}: Props) {
  const [openedStatusMenuId, setOpenedStatusMenuId] = useAtom(openedStatusMenuIdAtom);
  const isMenuOpen = openedStatusMenuId === jobChangeProcess.id;
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const setCurrentSelectjobChangeProcess = useSetAtom(currentSelectjobChangeProcessAtom);
  const setCurrentjobChangeProcessStatus = useSetAtom(currentjobChangeProcessStatusAtom);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => {
    setOpenedStatusMenuId((prev) => (prev === jobChangeProcess.id ? null : prev));
  }, [jobChangeProcess.id, setOpenedStatusMenuId]);

  const openMenu = useCallback(() => {
    setOpenedStatusMenuId(jobChangeProcess.id);
  }, [jobChangeProcess.id, setOpenedStatusMenuId]);

  // 利用可能なアクションがあるかどうかを判定
  const hasAvailableActions = useMemo(() => {
    return (
      jobChangeProcess.actions.is_able_to_progress ||
      jobChangeProcess.actions.is_able_to_offer ||
      jobChangeProcess.actions.is_able_to_close ||
      jobChangeProcess.actions['is_able_to_reject'] ||
      jobChangeProcess.actions.is_able_to_decline
    );
  }, [jobChangeProcess.actions]);

  // メニューが無効化される条件（辞退済み または 利用可能なアクションがない）
  const isDisabled = jobChangeProcess.progress_name === '辞退' || !hasAvailableActions;

  // メニューの位置を動的に計算
  useEffect(() => {
    if (isMenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const menuHeight = 200; // メニューの推定高さ

      setMenuRect(rect);

      // 下方向に表示した場合の位置をチェック
      const bottomSpace = viewportHeight - rect.bottom;
      // 上方向に表示した場合の位置をチェック
      const topSpace = rect.top;

      // 下方向に十分なスペースがない場合は上方向に表示
      if (bottomSpace < menuHeight && topSpace > menuHeight) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
  }, [isMenuOpen]);

  // メニュー外（ボタン・メニュー本体以外）をクリックしたら閉じる
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, closeMenu]);

  // メニューを開いている間は背景のスクロールを止めて画面を固定する
  useEffect(() => {
    if (!isMenuOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMenuOpen]);

  // スクロールしたらメニューを閉じる。
  // メニューは position: fixed で開いた位置に固定されるため、
  // ダッシュボード「未対応の選考」のように内側スクロールコンテナ上で開いた場合、
  // カードだけがスクロールしてメニューが取り残される。scroll はバブリングしないため
  // capture フェーズで購読し、内側コンテナのスクロールも検知して閉じる。
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleScroll = () => closeMenu();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isMenuOpen, closeMenu]);

  // 自分が開いた状態のままアンマウントされた場合は atom を解放しておく
  useEffect(() => {
    return () => {
      setOpenedStatusMenuId((prev) => (prev === jobChangeProcess.id ? null : prev));
    };
  }, [jobChangeProcess.id, setOpenedStatusMenuId]);

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        className={clsx(
          'flex h-[42px] w-full appearance-none items-center rounded border border-solid border-border px-3',
          isDisabled && 'cursor-not-allowed opacity-60'
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (isDisabled) return;
          if (isMenuOpen) {
            closeMenu();
          } else {
            openMenu();
          }
        }}
      >
        <p className="truncate text-xs">{triggerLabel ?? jobChangeProcess.progress_name}</p>
        <img
          className="ml-auto"
          src={`/agent/job_change_processes/icon-chevron-${isMenuOpen ? 'top' : 'down'}-black.svg`}
          alt=""
          width={20}
          height={20}
        />
      </button>
      {isMenuOpen &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            className={clsx(
              'fixed z-[9999] w-[180px] rounded bg-white shadow-[0px_0px_12px_0px_#00000033]',
              menuPosition === 'bottom' ? 'top-[46px]' : 'bottom-[46px]'
            )}
            style={{
              left: `${menuRect.left}px`,
              top: menuPosition === 'bottom' ? `${menuRect.bottom + 4}px` : undefined,
              bottom:
                menuPosition === 'top' ? `${window.innerHeight - menuRect.top + 4}px` : undefined,
            }}
          >
            {jobChangeProcess.actions.is_able_to_progress && (
              <div
                className="flex h-xxl w-full cursor-pointer items-center px-3 text-sm hover:opacity-60"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentjobChangeProcessStatus('progress');
                  setCurrentSelectjobChangeProcess(jobChangeProcess);
                  statusModalOpen();
                  closeMenu();
                }}
              >
                次の選考へ進む
              </div>
            )}
            {jobChangeProcess.actions.is_able_to_offer && (
              <div
                className="flex h-xxl w-full cursor-pointer items-center px-3 text-sm text-primary hover:opacity-60"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentjobChangeProcessStatus('offer');
                  setCurrentSelectjobChangeProcess(jobChangeProcess);
                  statusModalOpen();
                  closeMenu();
                }}
              >
                内定
              </div>
            )}
            {jobChangeProcess.actions.is_able_to_close && (
              <div
                className="flex h-xxl w-full cursor-pointer items-center px-3 text-sm text-primary hover:opacity-60"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentjobChangeProcessStatus('close');
                  setCurrentSelectjobChangeProcess(jobChangeProcess);
                  statusModalOpen();
                  closeMenu();
                }}
              >
                内定承諾
              </div>
            )}
            {jobChangeProcess.actions['is_able_to_reject'] && (
              <div
                className="flex h-xxl w-full cursor-pointer items-center px-3 text-sm text-danger hover:opacity-60"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentjobChangeProcessStatus('reject');
                  setCurrentSelectjobChangeProcess(jobChangeProcess);
                  statusModalOpen();
                  closeMenu();
                }}
              >
                不採用
              </div>
            )}
            {jobChangeProcess.actions.is_able_to_decline && (
              <div
                className="flex h-xxl w-full cursor-pointer items-center px-3 text-sm text-danger hover:opacity-60"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentjobChangeProcessStatus('decline');
                  setCurrentSelectjobChangeProcess(jobChangeProcess);
                  statusModalOpen();
                  closeMenu();
                }}
              >
                求職者辞退
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
